import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch} from 'react-redux';
import {
  Formik
} from 'formik';
import * as yup from 'yup';
import NumberFormat from 'react-number-format';
import {
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  Typography,
  MenuItem
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Breadcrumb,
  BackdropCircular,
  InfoTooltip
} from 'components';
import { createNewTaxRateApi } from 'api';
import { states } from 'helpers/usStates';


const useStyles = makeStyles(() => ({
  fieldWidths: {
    maxWidth: '602px',
  },
}));

const zipPostcodeRegRxp = /(^\d{5}$)|\*/;
const zipRangeRegRxp = /(^\d{5}$)/;


const AddNewTaxRate = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [checkedStatus, setCheckedStatus] = useState(false);

  const {
    loadingTaxRates,
    taxRule
  } = useSelector((state) => state.tax_rates);

  const { settingsActiveTab } = useSelector((state) => state.store_configs);

  const saveAndMoveToTaxRatesPage = async (values) => {
    await createNewTaxRateApi({dispatch, newTaxRate: values, taxRule});
    history.push({pathname: '/configurations/tax-rates'});
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div>
        <Breadcrumb
          routeSegments={settingsActiveTab === '#go_live_requirements' ?
            [
              { name: 'Settings', path: '/settings#go_live_requirements' },
              { name: 'Go Live Requirements', path: '/settings#go_live_requirements' },
              { name: 'Tax Rates', path: '/configurations/tax-rates' },
              { name: 'Add New Tax Rate' }
            ]
            :
            [
              { name: 'Settings', path: '/settings#store_configurations' },
              { name: 'Store Configurations', path: '/settings#store_configurations' },
              { name: 'Tax Rates', path: '/configurations/tax-rates' },
              { name: 'Add New Tax Rate' }
            ]}
        />
      </div>

      <Formik
        initialValues={{
          code: '',
          tax_postcode: '',
          tax_region_id: '',
          tax_country_id: '',
          rate: '',
          zip_is_range: 0,
          zip_from: '',
          zip_to: ''
        }}
        onSubmit={(values) => {
          saveAndMoveToTaxRatesPage(values);
        }}
        enableReinitialize
        validationSchema={
          yup.object().shape({
            code: yup
              .string()
              .required('Field is required'),
            tax_postcode: yup
              .string()
              .when('zip_is_range', {
                is: 0,
                then: yup
                  .string()
                  .matches(
                    zipPostcodeRegRxp,
                    'Please enter a valid zip code (Ex: 90602) or * to use taxes for all zip codes')
                  .required('Field is required')
              }),
            zip_is_range: yup
              .number(),
            zip_from: yup
              .string()
              .when('zip_is_range', {
                is: 1,
                then: yup
                  .string()
                  .matches(zipRangeRegRxp, 'Please enter a valid zip code (Ex: 90602)')
                  .required('Field is required')
              }),
            zip_to: yup
              .string()
              .when('zip_is_range', {
                is: 1,
                then: yup
                  .string()
                  .matches(zipRangeRegRxp, 'Please enter a valid zip code (Ex: 90602)')
                  .test('zip_to', 'Range To should be greater than Range From', function () {
                    const { zip_from, zip_to } = this.parent;
                    return !(zip_to && zip_from && zip_to <= zip_from);
                  })
                  .required('Field is required')
              }),
            tax_region_id: yup
              .string()
              .required('Field is required'),
            tax_country_id: yup
              .string()
              .required('Field is required'),
            rate: yup
              .number()
              .nullable().typeError('Allowed numbers only')
              .max(100, 'Rate Percent must not exceed 100%')
              .required('Field is required'),
          }, [
            ['zip_is_range', 'zip_from'],
            ['zip_is_range', 'zip_to'],
            ['zip_is_range', 'tax_postcode']
          ]
          )
        }
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        }) => {
          return (
            <div className="mx-auto mb-5 ">
              <form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Card elevation={1}  className="p-8 my-10 mx-auto max-w-685">
                  <Typography variant="h6" className="my-5">Please fill in the following details for tax rate configurations.</Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid container spacing={2} item xs={12}>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          className="my-2"
                          name="code"
                          value={values.code}
                          label="Tax Identifier"
                          variant="outlined"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.code && Boolean(errors.code)}
                          helperText={touched.code && errors.code}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.zip_is_range === 1}
                              name="zip_is_range"
                              color="primary"
                              onChange={(e) => {
                                setFieldValue('zip_is_range', e.target.checked ? 1 : 0);
                              }}
                            />
                          }
                          label="Zip/Post is Range"
                        />
                      </Grid>
                      {values.zip_is_range === 0 ?
                        <Grid item xs={12} className="flex">
                          <TextField
                            required
                            variant="outlined"
                            name="tax_postcode"
                            className="w-full"
                            label="Zip Code"
                            value={values.tax_postcode}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.tax_postcode && Boolean(errors.tax_postcode)}
                            helperText={touched.tax_postcode && errors.tax_postcode}
                          />
                          <div className="mt-5">
                            <InfoTooltip title="Enter a valid zip code (Ex: 90602) or * - matches any zip code" />
                          </div>
                        </Grid>
                        :
                        <>
                          <Grid item xs={12} className={classes.fieldWidths}>
                            <TextField
                              fullWidth
                              required
                              variant="outlined"
                              name="zip_from"
                              className="w-full mt-4"
                              label="Range From"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.zip_from && Boolean(errors.zip_from)}
                              helperText={errors.zip_from && errors.zip_from}
                            />
                          </Grid>
                          <Grid item xs={12} className={classes.fieldWidths}>
                            <TextField
                              fullWidth
                              required
                              variant="outlined"
                              name="zip_to"
                              className="w-full mt-4"
                              label="Range To"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.zip_to && Boolean(errors.zip_to)}
                              helperText={errors.zip_to && errors.zip_to}
                            />
                          </Grid>
                        </>
                      }
                      <Grid item xs={12} className={classes.fieldWidths}>
                        <TextField
                          select
                          required
                          fullWidth
                          className="my-2 "
                          name="tax_region_id"
                          label="State"
                          variant="outlined"
                          value={values.tax_region_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.tax_region_id && Boolean(errors.tax_region_id)}
                          helperText={touched.tax_region_id && errors.tax_region_id}
                        >
                          <MenuItem value={'0'}>Any State</MenuItem>
                          {states.map((state) => (
                            <MenuItem value={state.value} key={state.value}>
                              {state.name}
                            </MenuItem>))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} className={classes.fieldWidths}>
                        <TextField
                          select
                          required
                          fullWidth
                          className="my-2"
                          name="tax_country_id"
                          label="Country"
                          variant="outlined"
                          value={values.tax_country_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.tax_country_id && Boolean(errors.tax_country_id)}
                          helperText={touched.tax_country_id && errors.tax_country_id}
                        >
                          <MenuItem value={'US'}>United States</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} className={classes.fieldWidths}>
                        <NumberFormat
                          customInput={TextField}
                          allowNegative={false}
                          suffix="%"
                          fullWidth
                          required
                          variant="outlined"
                          name="rate"
                          className="w-full"
                          label="Rate Percent"
                          value={values.rate}
                          onValueChange={(value) => {
                            setFieldValue('rate', value.floatValue);
                          }}
                          onBlur={handleBlur}
                          error={touched.rate && Boolean(errors.rate)}
                          helperText={touched.rate && errors.rate}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} className="mt-10">
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          onChange={(e) => {
                            setCheckedStatus(e.target.checked);
                          }}
                        />
                      }
                      label={
                        <div>
                          For your convenience we use a tax look-up by zip code from the following Government
                          Source (<a href="https://taxfoundation.org/2021-sales-taxes/" target="_blank" rel="noreferrer">https://taxfoundation.org/2021-sales-taxes/ </a>). However, we require that you acknowledge
                          the Tax Rate that is used by your practice. By checking this box, you acknowledge and accept
                          the Tax Rate listed above.
                        </div>
                      }
                    />
                  </Grid>
                </Card>
                <Button
                  variant="contained"
                  color="primary"
                  className="mt-2 px-10 min-w-100"
                  disabled={!checkedStatus}
                  type="submit"
                >
                  Submit
                </Button>
              </form>
            </div>
          );
        }}
      </Formik>
      <BackdropCircular loading={loadingTaxRates} />
    </div>
  );
};

export default AddNewTaxRate;
